import React, { ReactNode, useState } from 'react';
import { AdvancedMarker, AdvancedMarkerProps } from '@vis.gl/react-google-maps';
import { MapPin } from '../../MapPin';
import { OrderStatus } from '@src/pages/Deliveries/context/orders';
import styles from '../OrderMapMarker/OrderMapMarker.module.scss';
import MapCard from '../../MapCard';
import { globalStyles } from 'snacks-design-system';
import { mapPinColorGenerator } from '../../MapPin/helpers';
import PIEText from '@src/shared/components/PIEText';
import { useTranslation } from 'react-i18next';
import {
  orderCourierStatusTranslationKeys,
  Statuses,
} from '@src/pages/DeliveryDashboard/models/model-constants';
import OrderMapMarkerIcon from '../OrderMapMarker/OrderMapMarkerIcon';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import dayjs from 'dayjs';
import { IconClockAlert, IconReceipt, IconReceiptSuccess } from '@justeattakeaway/pie-icons-react';
interface OrderMapMarkerProps {
  orderStatus: OrderStatus;
  showCard?: Boolean;
  insertElement?: ReactNode;
  dueTime: string | boolean;
  updateDate?: string;
  onClick?: () => void;
  isLateOrder?: boolean;
}

const { xdsColorContentLight, xdsColorSupportPositive } = globalStyles;

export const OrderMapMarker = ({
  orderStatus,
  position,
  onClick,
  showCard,
  dueTime,
  customerInformation,
  isLateOrder,
  updateDate,
  insertElement,
}: AdvancedMarkerProps & OrderMapMarkerProps) => {
  const { isResolutionMobile } = useDeviceDetect();
  const { t } = useTranslation();
  const getIcon = () => {
    const icons = {
      [OrderStatus.Assigned]: <IconReceiptSuccess size="xs" />,
      [OrderStatus.InDelivery]: <IconReceiptSuccess size="xs" />,
      [OrderStatus.Delivered]: <></>,
      [OrderStatus.Unassigned]: <IconReceipt size="xs" />,
      [OrderStatus.AssignedToNextPool]: <IconReceiptSuccess size="xs" />,
    };

    return icons[orderStatus] || <IconReceipt size="xs" />;
  };

  const getIconClassName = () => {
    if (orderStatus !== Statuses.Unassigned && orderStatus !== Statuses.Delivered) {
      return styles.OrderMapMarkerIconActive;
    } else if (orderStatus === Statuses.Unassigned) {
      return styles.OrderMapMarkerIconIdle;
    } else {
      return '';
    }
  };

  const customerInformationLimiter = (text: string) => {
    if (isResolutionMobile) {
      return text.length > 11 ? `${text.slice(0, 8)}...` : text;
    }
    return text.length > 19 ? `${text.slice(0, 16)}...` : text;
  };

  const renderDueTime = () => {
    if (isLateOrder) {
      return (
        <PIEComponentUtil marginLeft="var(--xds-spacing-b)">
          <PIETag
            className={styles.OrderMapMarkerLateTag}
            icon={isResolutionMobile ? <></> : <IconClockAlert size="xs" />}
            mode={ETagsColorType.Error}
            size={isResolutionMobile ? 'small' : 'large'}
          >
            {dueTime}
          </PIETag>
        </PIEComponentUtil>
      );
    } else {
      return (
        <PIEText
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-bold)"
          text={dueTime}
          customStyle={{ marginLeft: 'var(--xds-spacing-b)' }}
        />
      );
    }
  };
  return (
    <>
      {insertElement && <AdvancedMarker position={position}>{insertElement}</AdvancedMarker>}
      {orderStatus !== Statuses.Delivered ? (
        <AdvancedMarker
          position={position}
          onClick={() => {
            onClick && onClick();
          }}
        >
          <div className={styles.OrderMapMarker}>
            <div className={`${styles.OrderMapMarkerIconContainer} ${getIconClassName()}`}>
              {getIcon()}
            </div>
            <div style={{ width: 'max-content' }}>
              <PIEText
                size="var(--xds-font-size-14)"
                fontWeight="var(--xds-font-weight-regular)"
                text={customerInformationLimiter(customerInformation)}
              />
            </div>
            {renderDueTime()}
          </div>
        </AdvancedMarker>
      ) : (
        <>
          {showCard ? (
            <AdvancedMarker
              position={position}
              onClick={() => {
                onClick && onClick();
              }}
            >
              <MapCard backgroundColor={xdsColorContentLight}>
                <PIEComponentUtil marginTop={'var(--xds-spacing-a)'}>
                  <PIEText
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-regular)"
                    text={customerInformationLimiter(customerInformation)}
                    align="center"
                  />
                </PIEComponentUtil>

                <PIETag mode={ETagsColorType.Success} className={styles.OrderMapMarkerDeliveredTag}>
                  <PIEText
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-bold)"
                    text={`${t('order.deliveredAt')} ${dayjs(updateDate).format('HH:mm')}`}
                    align="center"
                  />
                </PIETag>
              </MapCard>
            </AdvancedMarker>
          ) : null}

          <AdvancedMarker
            clickable={true}
            position={position}
            onClick={() => {
              onClick && onClick();
            }}
          >
            <MapPin {...mapPinColorGenerator(xdsColorContentLight, xdsColorSupportPositive)}>
              <OrderMapMarkerIcon orderStatus={orderStatus} />
            </MapPin>
          </AdvancedMarker>
        </>
      )}
    </>
  );
};

export default OrderMapMarker;
